import { Controller } from "@hotwired/stimulus"
import { track } from "../lib/mixpanel"


export default class extends Controller {
  static targets = ["processedModal", "groupToggle", "groupLabel", "listLabel"]
  static values = {
    viewMode: String,
    viewModeUrl: String,
    updateNoteUrl: String,
    markProcessedUrl: String,
    markUnprocessedUrl: String
  }

  connect() {
    this._sortOrders = {}
    this._restoreGrouping()
  }

  // ── Group by project (Phelipe 2026-09-16) ───────────────────────────
  // Every table on the page sorts its rows by project and gets a header
  // row per project (name + count). Rows keep a stable order inside a
  // group, so Days-pending sorting still works within each project.
  // Remembered per browser.

  toggleGroup() {
    const on = !this._grouped
    this._applyGrouping(on)
    // Saved on the user, so the choice follows them to any browser.
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    if (this.hasViewModeUrlValue) {
      fetch(this.viewModeUrlValue, {
        method: "POST",
        headers: { "X-CSRF-Token": csrfToken, "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({ mode: on ? "project" : "list" })
      }).catch(() => {})
    }
    track("Pending Items Grouped By Project", { on })
  }

  _restoreGrouping() {
    if (!this.hasGroupToggleTarget) return
    this._applyGrouping(this.viewModeValue === "project")
  }

  _applyGrouping(on) {
    this._grouped = on
    if (this.hasGroupToggleTarget) this.groupToggleTarget.setAttribute("aria-pressed", on ? "true" : "false")
    // The button offers the other view.
    if (this.hasGroupLabelTarget) this.groupLabelTarget.style.display = on ? "none" : ""
    if (this.hasListLabelTarget) this.listLabelTarget.style.display = on ? "" : "none"
    this.element.querySelectorAll("table").forEach(table => {
      const tbody = table.querySelector("tbody")
      if (!tbody) return
      const rows = [...tbody.querySelectorAll("tr[data-project-id]")]
      if (!rows.length) return
      tbody.querySelectorAll("tr.js-project-group").forEach(r => r.remove())
      // First time through, remember the server's order so "off" restores it.
      rows.forEach((r, i) => { if (r.dataset.origIndex == null) r.dataset.origIndex = String(i) })
      if (!on) {
        rows.sort((a, b) => Number(a.dataset.origIndex) - Number(b.dataset.origIndex))
        rows.forEach(r => tbody.appendChild(r))
        return
      }
      const cols = table.querySelectorAll("thead th").length || 1
      const groups = new Map()
      rows.forEach(r => {
        const key = r.dataset.projectId || ""
        if (!groups.has(key)) groups.set(key, { name: r.dataset.projectName || "", rows: [] })
        groups.get(key).rows.push(r)
      })
      const ordered = [...groups.values()].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }))
      ordered.forEach(g => {
        const head = document.createElement("tr")
        head.className = "js-project-group bg-gray-100 border-b border-gray-200"
        const td = document.createElement("td")
        td.colSpan = cols
        td.className = "px-2 xl:px-3 py-1.5 text-[13px] font-semibold text-gray-600"
        td.textContent = `${g.name || "—"} · ${g.rows.length}`
        head.appendChild(td)
        tbody.appendChild(head)
        g.rows.forEach(r => tbody.appendChild(r))
      })
    })
  }

  // Row click — open URL in new tab (skip clicks on checkboxes, editables, buttons)
  navigateToRow(event) {
    if (event.target.closest("input, button, [contenteditable]")) return
    const url = event.currentTarget.dataset.url
    if (url) window.open(url, "_blank")
  }

  // Prevent row click propagation from interactive elements
  stopPropagation(event) {
    event.stopPropagation()
  }

  // Click any column header to sort that table (Phelipe 2026-09-16),
  // ascending first, again for descending. Cells may carry a
  // data-sort-value (ISO date, raw number); otherwise their text is used.
  // Grouped view keeps its groups and sorts inside each.
  sortBy(event) {
    const th = event.currentTarget
    const table = th.closest("table")
    const tbody = table && table.querySelector("tbody")
    if (!tbody) return
    const idx = th.cellIndex
    const type = th.dataset.sortType || "text"
    const key = `${table.id || ""}:${idx}`
    const dir = this._sortOrders[key] === "asc" ? "desc" : "asc"
    this._sortOrders[key] = dir

    const valueOf = (row) => {
      const td = row.cells[idx]
      if (!td) return ""
      const raw = td.dataset.sortValue != null ? td.dataset.sortValue : td.textContent
      const v = String(raw).trim()
      if (type === "number") { const n = parseFloat(v.replace(/[^0-9.\-]/g, "")); return isNaN(n) ? Number.NEGATIVE_INFINITY : n }
      if (type === "date") { const t = Date.parse(v); return isNaN(t) ? Number.NEGATIVE_INFINITY : t }
      return v.toLowerCase()
    }
    const rows = [...tbody.querySelectorAll("tr")].filter(r => !r.classList.contains("js-project-group"))
    rows.sort((a, b) => {
      const va = valueOf(a), vb = valueOf(b)
      const cmp = typeof va === "number" ? va - vb : va.localeCompare(vb, undefined, { numeric: true, sensitivity: "base" })
      return dir === "asc" ? cmp : -cmp
    })
    rows.forEach(row => tbody.appendChild(row))

    table.querySelectorAll("thead [data-sort-indicator]").forEach(el => { el.textContent = "" })
    const ind = th.querySelector("[data-sort-indicator]")
    if (ind) ind.textContent = dir === "asc" ? "\u25B2" : "\u25BC"

    // Grouped: keep the groups, with the new order inside each.
    if (this._grouped) this._applyGrouping(true)
  }

  // AA: Toggle all checkboxes in unprocessed approved financials
  selectAllAa() {
    const checkboxes = this.element.querySelectorAll(".aa-unprocessed-row .aa-checkbox")
    const allChecked = Array.from(checkboxes).every(cb => cb.checked)
    checkboxes.forEach(cb => { cb.checked = !allChecked })
  }

  // AA: Mark checked items as processed
  markProcessed() {
    const checkboxes = this.element.querySelectorAll("#unprocessed-financials .aa-checkbox")
    const dataArray = []

    checkboxes.forEach(cb => {
      if (cb.checked) {
        dataArray.push({
          project_id: cb.dataset.projectId,
          type: cb.dataset.type,
          sequence: cb.dataset.sequence
        })
      }
    })

    if (dataArray.length === 0) return

    if (!confirm("Selected items will be marked as processed. You can view them later by clicking 'View Processed Financials'.")) return

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    fetch(this.markProcessedUrlValue, {
      method: "POST",
      headers: {
        "X-CSRF-Token": csrfToken,
        "Content-Type": "application/x-www-form-urlencoded"
      },
      body: `data=${encodeURIComponent(JSON.stringify(dataArray))}`
    }).then(response => {
      if (response.ok) {
        // Remove processed rows from DOM
        checkboxes.forEach(cb => {
          if (cb.checked) {
            const row = cb.closest("tr")
            if (row) row.remove()
          }
        })
        // Update count badges
        this._updateSectionCounts()
      }
    })
  }

  // AA: Save note on blur
  saveNote(event) {
    const el = event.currentTarget
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    fetch(this.updateNoteUrlValue, {
      method: "POST",
      headers: {
        "X-CSRF-Token": csrfToken,
        "Content-Type": "application/x-www-form-urlencoded"
      },
      body: new URLSearchParams({
        project_id: el.dataset.projectId,
        type: el.dataset.type,
        sequence: el.dataset.sequence,
        text: el.textContent
      })
    })
  }

  // AA: Unprocess item (from processed modal)
  unprocessItem(event) {
    const cb = event.currentTarget
    if (cb.checked) return // Only act on uncheck

    if (!confirm("This item will be moved back to unprocessed.")) {
      cb.checked = true
      return
    }

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    fetch(this.markUnprocessedUrlValue, {
      method: "POST",
      headers: {
        "X-CSRF-Token": csrfToken,
        "Content-Type": "application/x-www-form-urlencoded"
      },
      body: new URLSearchParams({
        project_id: cb.dataset.projectId,
        type: cb.dataset.type,
        sequence: cb.dataset.sequence
      })
    }).then(response => {
      if (response.ok) {
        const row = cb.closest("tr")
        if (row) row.style.display = "none"
      }
    })
  }

  // Modal: open
  openProcessedModal() {
    if (this.hasProcessedModalTarget) {
      this.processedModalTarget.classList.remove("hidden")
      document.body.style.overflow = "hidden"
    }
  }

  // Modal: close
  closeProcessedModal() {
    if (this.hasProcessedModalTarget) {
      this.processedModalTarget.classList.add("hidden")
      document.body.style.overflow = ""
    }
  }

  // Update section count badges after DOM changes
  _updateSectionCounts() {
    const unprocessedTable = document.getElementById("unprocessed-financials")
    if (unprocessedTable) {
      const count = unprocessedTable.querySelectorAll("tbody tr").length
      const badge = unprocessedTable.closest(".mb-6")?.querySelector("h2 span")
      if (badge) badge.textContent = count
    }
  }
}
